import { useCallback, useEffect, useRef, useState } from 'react';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { reorder } from '@atlaskit/pragmatic-drag-and-drop/reorder';
import {
  extractClosestEdge,
  Edge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { getReorderDestinationIndex } from '@atlaskit/pragmatic-drag-and-drop-hitbox/util/get-reorder-destination-index';
import * as liveRegion from '@atlaskit/pragmatic-drag-and-drop-live-region';
import { ColumnKey, COLUMN_LABELS } from './columnDefs';
import { isItemData } from './itemData';

/**
 * List-level drag-and-drop concerns for a small, fixed column-order list:
 * the drop-completion monitor, hover-edge state, and screen-reader
 * announcements. Per-row draggable/dropTarget registration lives in
 * `useColumnOrderRow`, called by each row's own component — keeping
 * registration tied to a `useEffect` (not a ref callback) so cleanup runs
 * reliably regardless of React version.
 */
export function useColumnOrderDnd(
  order: ColumnKey[],
  onReorder: (newOrder: ColumnKey[]) => void,
  disabled: boolean,
) {
  const [closestEdge, setClosestEdgeState] = useState<
    Partial<Record<ColumnKey, Edge | null>>
  >({});
  const orderRef = useRef(order);
  orderRef.current = order;
  // `onReorder` is a plain inline function in the caller (DisplaySettingsTab),
  // recreated every render — keeping it out of the monitor effect's
  // dependency array (read via this ref instead) means the drop monitor
  // isn't torn down and re-registered on every unrelated re-render, which
  // could otherwise interrupt an in-progress drag.
  const onReorderRef = useRef(onReorder);
  onReorderRef.current = onReorder;

  const setClosestEdge = useCallback((columnKey: ColumnKey, edge: Edge | null) => {
    setClosestEdgeState((prev) =>
      prev[columnKey] === edge ? prev : { ...prev, [columnKey]: edge },
    );
  }, []);

  const announceMove = useCallback(
    (columnKey: ColumnKey, _fromIndex: number, toIndex: number, total: number) => {
      liveRegion.announce(
        `${COLUMN_LABELS[columnKey]} moved to position ${toIndex + 1} of ${total}.`,
      );
    },
    [],
  );

  useEffect(() => {
    if (disabled) {
      return undefined;
    }

    return monitorForElements({
      canMonitor: ({ source }) => isItemData(source.data),
      onDrop: ({ location, source }) => {
        const target = location.current.dropTargets[0];
        if (!target) {
          return;
        }

        const sourceData = source.data;
        const targetData = target.data;
        if (!isItemData(sourceData) || !isItemData(targetData)) {
          return;
        }

        const currentOrder = orderRef.current;
        const startIndex = currentOrder.indexOf(sourceData.columnKey);
        const indexOfTarget = currentOrder.indexOf(targetData.columnKey);
        if (startIndex < 0 || indexOfTarget < 0) {
          return;
        }

        const closestEdgeOfTarget = extractClosestEdge(targetData);
        const finishIndex = getReorderDestinationIndex({
          startIndex,
          indexOfTarget,
          closestEdgeOfTarget,
          axis: 'vertical',
        });
        if (finishIndex === startIndex) {
          return;
        }

        const newOrder = reorder({
          list: currentOrder,
          startIndex,
          finishIndex,
        });
        onReorderRef.current(newOrder);
        announceMove(
          sourceData.columnKey,
          startIndex,
          finishIndex,
          newOrder.length,
        );
      },
    });
  }, [disabled, announceMove]);

  useEffect(() => {
    return () => liveRegion.cleanup();
  }, []);

  return { closestEdge, setClosestEdge, announceMove };
}
